import React, { useEffect, useState } from "react";
import { getStaffShifts } from "../services/shifts";
import { getServiceUserSingleMedicationGroup } from "../services/users";
import {
  getAdministeredMedication,
  updateAdministeredMedication,
  administerMedication,
} from "../services/medication";
import { useParams, useNavigate } from "react-router-dom";
import { Cards } from "../components/Cards.tsx";
import { useForm } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Spinner from "../components/PageLoader.js";
import {
  capitalize,
  formatUKTime,
  formatUTCForDB,
  convertLocalToUTC,
} from "../utils/functions.js";
import toast from "react-hot-toast";
import { MedicationAdministrationModal } from "../components/modals/MedicationAdministrationModal.js";

const validationSchema = Yup.object().shape({
  selectedMedications: Yup.array()
    .min(1, "Please select at least one medication")
    .required("Please select at least one medication"),
  administrationType: Yup.string().required("Administration type is required"),
  signature: Yup.string().required("Signature is required"),
  notes: Yup.string().required("Notes are required"),
});

export const FieldShiftMedication = () => {
  const [shift, setShift] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [medicationGroup, setMedicationGroup] = useState(null);
  const [medicationGroupDeleted, setMedicationGroupDeleted] = useState(false);
  const [selectedMedications, setSelectedMedications] = useState([]);
  const [administeredMedications, setAdministeredMedications] = useState([]);
  const [showAdministrationModal, setShowAdministrationModal] = useState(false);
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem("user"));
  const urlParam = useParams();
  const shiftId = urlParam.id;
  const serviceUserId = urlParam.serviceUserId;

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      selectedMedications: [],
      administrationType: "",
      signature: "",
      notes: "",
    },
  });

  const fetchShift = async () => {
    try {
      const { data } = await getStaffShifts({ userId: user.username || "" });
      const currentShift = data.find(
        (shift) => shift.shiftId === Number(shiftId)
      );
      setShift(currentShift);

      return currentShift;
    } catch (error) {
      toast.error("Failed to fetch shift details");
      return null;
    }
  };

  const fetchSpecificMedicationGroup = async (groupId) => {
    try {
      const { data } = await getServiceUserSingleMedicationGroup(
        serviceUserId,
        groupId
      );
      setMedicationGroup(data);
      setMedicationGroupDeleted(false);
    } catch (error) {
      // Don't show error toast for deleted medication groups
      // Still allow the component to render and show administration history
      setMedicationGroupDeleted(true);
      setMedicationGroup(null);
    }
  };

  const fetchAdministeredMedications = async () => {
    try {
      const { data } = await getAdministeredMedication(shiftId,serviceUserId);
      setAdministeredMedications(data?.administeredMedications);
    } catch (error) {
      toast.error("Failed to fetch administered medications");
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const shiftData = await fetchShift();
      if (shiftData?.medicationGroupId) {
        await fetchSpecificMedicationGroup(shiftData.medicationGroupId);
      }
    } catch (error) {
      // Error handled by individual fetch functions
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    Promise.all([fetchData(), fetchAdministeredMedications()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceUserId]);

  const handleMedicationSelect = (medicationName) => {
    const isSelected = selectedMedications.includes(medicationName);
    let newSelection;

    if (isSelected) {
      newSelection = selectedMedications.filter(
        (name) => name !== medicationName
      );
    } else {
      newSelection = [...selectedMedications, medicationName];
    }

    setSelectedMedications(newSelection);
    setValue("selectedMedications", newSelection);
  };

  const handleSelectAll = () => {
    if (!medicationGroup?.medications) return;

    // Only select medications that haven't been administered yet
    const availableMedications = medicationGroup.medications.filter(
      (med) => !isAdministered(med.id)
    );

    if (selectedMedications.length === availableMedications.length) {
      setSelectedMedications([]);
      setValue("selectedMedications", []);
    } else {
      const availableMedNames = availableMedications.map((med) => med.id);
      setSelectedMedications(availableMedNames);
      setValue("selectedMedications", availableMedNames);
    }
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    if (administeredMedications.length > 0) {
      try {
        const selectedMeds = medicationGroup.medications.filter((med) =>
          data.selectedMedications.includes(med.id)
        );

        for (const medication of selectedMeds) {
          const updatePayload = {
            shiftId: shift.shiftId,
            serviceUserId: serviceUserId,
            medicationItemId: medication.id,
            administrationDetails: {
              administrationType: data.administrationType,
              signature: data.signature,
              notes: data.notes,
              timestamp: formatUTCForDB(
                convertLocalToUTC(new Date()),
                "yyyy-MM-dd HH:mm:ss"
              ),
              administeredBy: user.username,
            },
          };

          await updateAdministeredMedication(updatePayload, serviceUserId);
        }

        await fetchAdministeredMedications();
        setShowAdministrationModal(false);

        const remainingSelected = selectedMedications.filter(
          (medId) => !selectedMeds.some((med) => med.id === medId)
        );
        setSelectedMedications(remainingSelected);
        setValue("selectedMedications", remainingSelected);

        toast.success("Medications administered successfully");
      } catch (error) {
        toast.error("Failed to record medication administration");
      } finally {
        setIsLoading(false);
      }
    } else {
      try {
        const selectedMeds = medicationGroup.medications.filter((med) =>
          data.selectedMedications.includes(med.id)
        );

        // Create new administration records for selected medications
        const newAdministrationRecords = selectedMeds.map((med) => ({
          medication: {
            id: med.id,
            name: med.name,
            dose: med.dose,
            quantity: med.quantity,
            route: med.route,
            instructions: med.instructions,
            form: med.form,
          },
          administrationDetails: {
            administrationType: data.administrationType,
            signature: data.signature,
            notes: data.notes,
            timestamp: formatUTCForDB(
              convertLocalToUTC(new Date()),
              "yyyy-MM-dd HH:mm:ss"
            ),
            administeredBy: user.username,
          },
        }));

        // Create the new JSON structure
        const administrationPayload = {
          shiftId: shift.shiftId.toString(),
          serviceUserId: serviceUserId,
          medicationGroup: medicationGroup.name,
          administeredMedications: newAdministrationRecords,
        };

        await administerMedication(administrationPayload, serviceUserId);

        fetchAdministeredMedications();
        setShowAdministrationModal(false);

        // Clear selection for administered medications
        const remainingSelected = selectedMedications.filter(
          (medId) => !selectedMeds.some((med) => med.id === medId)
        );
        setSelectedMedications(remainingSelected);
        setValue("selectedMedications", remainingSelected);

        toast.success("Medication administered successfully");
      } catch (error) {
        toast.error("Failed to record medication administration");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const isAdministered = (medicationId) => {
    return administeredMedications.some(
      (admin) => admin.medication?.id === medicationId
    );
  };

  if (isLoading && !medicationGroup) {
    return (
      <div className="documents__loader">
        <Spinner />
      </div>
    );
  }

  // Show "No Medication Available" only if there's no medication group AND no administration history
  const hasNoMedicationAndNoHistory = (
    !medicationGroup ||
    !medicationGroup.medications ||
    medicationGroup.medications.length === 0
  ) && (!administeredMedications || administeredMedications.length === 0);

  if (hasNoMedicationAndNoHistory && !medicationGroupDeleted) {
    return (
      <div className="field-shift__empty">
        <Cards>
          <div className="text-center">
            <h5 className="mb-16">No Medication Available</h5>
            <p style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
              {shift?.medicationGroupId
                ? `No medication found in the assigned medication group.`
                : "No medication group assigned to this shift."}
            </p>
            <button
              className="btn btn__secondary mt-16"
              onClick={() =>
                navigate(`/field-shifts/${shiftId}/${serviceUserId}`)
              }
            >
              Go Back
            </button>
          </div>
        </Cards>
      </div>
    );
  }

  // Show message for deleted medication group with no administration history
  if (medicationGroupDeleted && (!administeredMedications || administeredMedications.length === 0)) {
    return (
      <div className="field-shift__empty">
        <Cards>
          <div className="text-center">
            <h5 className="mb-16" style={{ color: "var(--status-warning)" }}>
              Medication Group Deleted
            </h5>
            <p style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
              The medication group assigned to this shift has been deleted and there is no administration history to display.
            </p>
            <button
              className="btn btn__secondary mt-16"
              onClick={() =>
                navigate(`/field-shifts/${shiftId}/${serviceUserId}`)
              }
            >
              Go Back
            </button>
          </div>
        </Cards>
      </div>
    );
  }

  return (
    <div className="field-shift__medication">
      {isLoading && (
        <div className="documents__loader">
          <Spinner />
        </div>
      )}

      {/* Only show medication selection if medication group exists and is not deleted */}
      {medicationGroup && !medicationGroupDeleted && medicationGroup.medications && medicationGroup.medications.length > 0 && (
        <Cards classes="mb-24">
          <div className="field-shift__prn-header">
            <h5 className="mb-16">
              {capitalize(medicationGroup.name)} Medication
            </h5>
            <button
              type="button"
              className="btn btn--sm"
              onClick={handleSelectAll}
            >
              {(() => {
                const availableMedications = medicationGroup.medications.filter(
                  (med) => !isAdministered(med.id)
                );
                return selectedMedications.length === availableMedications.length
                  ? "Deselect All"
                  : "Select All";
              })()}
            </button>
          </div>

          <div className="field-shift__prn-list">
            {medicationGroup.medications.map((medication, index) => (
              <div
                key={medication.id || index}
                className={`field-shift__prn-item ${
                  isAdministered(medication.id) ? "administered" : ""
                }`}
              >
                <div className="custom-checkbox">
                  <input
                    type="checkbox"
                    id={`med-${index}`}
                    className="custom-checkbox__input"
                    checked={selectedMedications.includes(medication.id)}
                    onChange={() => handleMedicationSelect(medication.id)}
                    disabled={isAdministered(medication.id)}
                  />
                  <label
                    htmlFor={`med-${index}`}
                    className="custom-checkbox__label"
                  >
                    <div className="custom-checkbox__box">
                      {selectedMedications.includes(medication.id) && (
                        <svg
                          className="custom-checkbox__icon"
                          viewBox="0 0 16 16"
                          fill="white"
                        >
                          <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" />
                        </svg>
                      )}
                    </div>
                  </label>
                </div>

                <div className="field-shift__prn-details">
                  <div className="field-shift__prn-name">
                    {medication.name}

                    {isAdministered(medication.id) && (
                      <span className="field-shift__administered-badge">
                        {" - "}Administered
                      </span>
                    )}
                  </div>
                  <div className="field-shift__prn-info">
                    <span className="field-shift__prn-dose">
                      {medication.dose}
                    </span>
                    <span className="field-shift__prn-quantity">
                      • {medication.quantity}
                    </span>
                    <span className="field-shift__prn-route">
                      • {capitalize(medication.route)}
                    </span>
                  </div>
                  {medication.instructions && (
                    <div className="field-shift__prn-instructions">
                      {medication.instructions}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Cards>
      )}

      {/* Show message when medication group is deleted but has administration history */}
      {medicationGroupDeleted && administeredMedications.length > 0 && (
        <Cards classes="mb-24">
          <div className="text-center" style={{ padding: "20px" }}>
            <h5 className="mb-16" style={{ color: "var(--status-warning)" }}>
              Medication Group Deleted
            </h5>
            <p style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
              The medication group assigned to this shift has been deleted, but administration history is preserved below for audit purposes.
            </p>
          </div>
        </Cards>
      )}

      {/* Floating Action Button */}
      {selectedMedications.length > 0 && (
        <div className="field-shift__prn-floating-action">
          <button
            type="button"
            className="btn btn__primary btn--floating"
            onClick={() => setShowAdministrationModal(true)}
          >
            Administer {selectedMedications.length} medication
            {selectedMedications.length > 1 ? "s" : ""}
          </button>
        </div>
      )}

      {/* Administration History - Always show for audit purposes, even if medications are deleted */}
      {administeredMedications.length > 0 && (
        <Cards classes="mb-24">
          <h5 className="mb-16">Administration History</h5>
          <div className="field-shift__administered-list">
            {administeredMedications.map((admin, index) => {
              // Handle deleted medications - preserve audit trail
              const medicationName = admin.medication?.name ||
                                   admin.medicationName ||
                                   `[Deleted Medication ID: ${admin.medication?.id || 'Unknown'}]`;

              const isDeletedMedication = !admin.medication?.name && !admin.medicationName;

              return (
                <div key={index} className="field-shift__administered-item">
                  <div>
                    <span
                      className="field-shift__administered-name"
                      style={isDeletedMedication ? {
                        opacity: 0.7,
                        fontStyle: 'italic',
                        color: 'var(--text-secondary)'
                      } : {}}
                    >
                      {medicationName}
                      {isDeletedMedication && (
                        <span
                          style={{
                            marginLeft: '8px',
                            fontSize: '12px',
                            color: 'var(--status-warning)',
                            backgroundColor: 'var(--status-warning-bg, #fff3cd)',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            border: '1px solid var(--status-warning)',
                            fontWeight: 'normal'
                          }}
                          title="This medication has been deleted but administration record is preserved for audit purposes"
                        >
                          Deleted
                        </span>
                      )}
                    </span>

                    {/* Show medication details if available */}
                    {admin.medication && (admin.medication.dose || admin.medication.quantity || admin.medication.route || admin.medication.instructions) && (
                      <div className="field-shift__prn-info" style={{ marginTop: '8px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                        {admin.medication.dose && (
                          <span className="field-shift__prn-dose">
                            {admin.medication.dose}
                          </span>
                        )}
                        {admin.medication.quantity && (
                          <span className="field-shift__prn-quantity">
                            • {admin.medication.quantity}
                          </span>
                        )}
                        {admin.medication.route && (
                          <span className="field-shift__prn-route">
                            • {capitalize(admin.medication.route)}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Show instructions if available */}
                    {admin.medication?.instructions && (
                      <div className="field-shift__prn-instructions" style={{ marginTop: '8px', fontSize: '14px', fontStyle: 'italic' }}>
                        {admin.medication.instructions}
                      </div>
                    )}

                    <div className="field-shift__administered-details">
                      <p>
                        Type:{" "}
                        {admin.administrationDetails?.administrationType ||
                          admin.administrationType}
                      </p>
                      <p>
                        By:{" "}
                        {admin.administrationDetails?.signature ||
                          admin.signature}
                      </p>
                      <p>
                        Time:{" "}
                        {formatUKTime(
                          admin.administrationDetails?.timestamp ||
                            admin.timestamp,
                          "dd/MM/yyyy HH:mm"
                        )}
                      </p>
                    </div>
                    {(admin.administrationDetails?.notes || admin.notes) && (
                      <div className="field-shift__administered-notes">
                        {admin.administrationDetails?.notes || admin.notes}
                      </div>
                    )}
                  </div>
                  <span className="field-shift__administered-status">
                    Administered
                  </span>
                </div>
              );
            })}
          </div>
        </Cards>
      )}

      {/* Medication Administration Modal */}
      {showAdministrationModal && (
        <MedicationAdministrationModal
          isOpen={showAdministrationModal}
          onClose={() => setShowAdministrationModal(false)}
          onSubmit={handleSubmit(onSubmit)}
          control={control}
          errors={errors}
          isLoading={isLoading}
          selectedCount={selectedMedications.length}
          medicationType="Medication"
        />
      )}
    </div>
  );
};
